import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSharedScroll } from "@/hooks/useSharedScroll";

type NavItem = { label: string; href: string; kind: "hash" | "route" };

const NAV_ITEMS: NavItem[] = [
  { label: "Home", href: "#home", kind: "hash" },
  { label: "Services", href: "/services", kind: "route" },
  { label: "About Us", href: "/about", kind: "route" },
  { label: "Contacts", href: "/contacts", kind: "route" },
];

export default function Navbar() {
  const [activeHref, setActiveHref] = useState<string>("#home");
  const activeRef = useRef<string>("#home");

  // Cache section offsetTop values so the scroll callback never touches
  // getBoundingClientRect (which would force a synchronous layout).
  const sectionTopsRef = useRef<Array<{ href: string; top: number; height: number }>>([]);

  useEffect(() => {
    const measure = () => {
      const tops: Array<{ href: string; top: number; height: number }> = [];
      for (const item of NAV_ITEMS) {
        if (item.kind !== "hash") continue;
        const el = document.getElementById(item.href.replace("#", ""));
        if (!el) continue;
        let top = 0;
        let node: HTMLElement | null = el;
        while (node) {
          top += node.offsetTop;
          node = node.offsetParent as HTMLElement | null;
        }
        tops.push({ href: item.href, top, height: el.offsetHeight });
      }
      sectionTopsRef.current = tops;
    };
    measure();
    window.addEventListener("resize", measure);
    // Re-measure after the initial load + a short delay so any fonts/images
    // finishing layout don't leave us with stale offsets.
    const t = window.setTimeout(measure, 400);
    return () => {
      window.removeEventListener("resize", measure);
      window.clearTimeout(t);
    };
  }, []);

  // Scroll-based active section highlight — driven by the shared global
  // broadcaster, so no per-component listener + no rAF here.
  useSharedScroll((scrollY, viewportH) => {
    const threshold = viewportH * 0.3;
    let best = "#home";
    let bestTop = -Infinity;
    const tops = sectionTopsRef.current;
    for (let i = 0; i < tops.length; i++) {
      // top of section relative to viewport = absoluteTop - scrollY
      const top = tops[i].top - scrollY;
      if (top <= threshold && top > bestTop) {
        bestTop = top;
        best = tops[i].href;
      }
    }
    if (best !== activeRef.current) {
      activeRef.current = best;
      setActiveHref(best);
    }
  });

  return (
    <header className="absolute top-0 left-0 right-0 z-50 pt-6 md:pt-8 px-6 md:px-10">
      <div className="flex items-center justify-between w-full max-w-[1600px] mx-auto">
        {/* Brand mark */}
        <a
          href="#home"
          aria-label="Serene home"
          className="group relative flex items-center justify-center w-11 h-11 rounded-full border border-foreground-950/25 bg-background-50/40 backdrop-blur-md cursor-pointer transition-colors duration-300 hover:bg-background-50/70"
        >
          <span className="font-heading text-[11px] tracking-[0.14em] font-medium text-foreground-950">
            SM
          </span>
        </a>

        {/* Center pill nav */}
        <nav className="hidden md:block">
          <ul className="flex items-center gap-1 rounded-full border border-foreground-950/20 bg-background-50/40 backdrop-blur-md px-1.5 py-1.5">
            {NAV_ITEMS.map((item) => {
              const isActive = item.kind === "hash" && item.href === activeHref;
              const baseClasses = `relative flex items-center justify-center px-5 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase font-medium whitespace-nowrap cursor-pointer transition-colors duration-300 ${
                isActive
                  ? "bg-foreground-950 text-background-50"
                  : "text-foreground-950/80 hover:text-foreground-950"
              }`;
              return (
                <li key={item.href}>
                  {item.kind === "route" ? (
                    <Link to={item.href} className={baseClasses}>
                      {item.label}
                    </Link>
                  ) : (
                    <a href={item.href} className={baseClasses}>
                      {item.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right side — social */}
        <div className="flex items-center gap-2">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label="Instagram"
            className="w-11 h-11 flex items-center justify-center rounded-full border border-foreground-950/25 bg-background-50/40 backdrop-blur-md text-foreground-950 cursor-pointer transition-colors duration-300 hover:bg-background-50/70"
          >
            <i className="ri-instagram-line text-base"></i>
          </a>
        </div>
      </div>
    </header>
  );
}